import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, type Outbox } from '@/lib/api/auth';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Info } from '@/design-system/Info';

const CHIP: Record<string, string> = {
  entregue: 'good',
  processado: 'good',
  enviando: 'warn',
  pendente: 'neutral',
  aguardando: 'neutral',
  falha: 'crit',
  erro: 'crit',
  // Não saiu por decisão de conformidade (opt-out, teto mensal), não por erro.
  cancelado: 'warn',
};

const carimbo = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

export function OutboxPage() {
  const token = useAuthStore((s) => s.token) || '';
  const [statusFiltro, setStatusFiltro] = useState('');

  const queryClient = useQueryClient();

  /**
   * Reenfileira o que falhou.
   *
   * Nao alcanca eventos cancelados: 'cancelado' marca o que o sistema decidiu
   * nao enviar (opt-out revogado, teto mensal atingido). Reprocessar isso
   * mandaria mensagem para quem pediu para nao receber.
   */
  const reprocessar = useMutation({
    mutationFn: (id?: string) => authApi.reprocessarOutbox(token, id),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['outbox'] });
      toast(
        r.reenfileirados === 0
          ? 'Nenhum evento em falha para reprocessar.'
          : `${r.reenfileirados} evento(s) devolvido(s) para a fila.`,
      );
    },
    onError: (err: any) => {
      const m = err?.response?.data?.message;
      toast(Array.isArray(m) ? m.join(' · ') : m || 'Erro ao reprocessar.');
    },
  });

  const { data: apiMensagens, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['outbox'],
    queryFn: () => authApi.getOutbox(token),
    enabled: !!token,
  });

  // Fila vazia e uma informacao legitima -- significa que nada esta pendente.
  // Preencher com exemplos fabricados faria a tela relatar disparos que nunca
  // existiram, e o botao de reprocessar contaria falhas inventadas.
  const mensagens = apiMensagens ?? [];

  const status = [...new Set(mensagens.map((m) => m.status))];
  const lista = mensagens.filter((m) => !statusFiltro || m.status === statusFiltro);

  const contar = (s: string) => mensagens.filter((m) => m.status === s).length;
  const comErro = mensagens.filter((m) => m.status === 'falha' || m.status === 'erro').length;
  // KPI único "Falhas com retry" (proto:790): une quem já esgotou as
  // tentativas (status falha/erro) com quem ainda está no ciclo de
  // retentativa (retries > 0) — o protótipo trata os dois como um só sinal.
  const falhasComRetry = mensagens.filter(
    (m) => m.status === 'falha' || m.status === 'erro' || (m.retries ?? 0) > 0,
  ).length;

  const HORA_MS = 60 * 60 * 1000;
  const agora = Date.now();
  // "Despachados · 24h" (proto:790): o protótipo crava '14.203' como número
  // de demonstração; aqui contamos de verdade quantos dos eventos
  // processados carregados têm updated_at nas últimas 24h. Limitação: a API
  // devolve só os últimos 50 eventos do mandato, então esta contagem pode
  // ficar abaixo do real se houver mais de 50 despachos em 24h.
  const despachados24h = mensagens.filter((m) => {
    if (m.status !== 'processado') return false;
    const ts = new Date(m.updated_at).getTime();
    return !Number.isNaN(ts) && agora - ts <= 24 * HORA_MS;
  }).length;

  const destinoDe = (m: Outbox) =>
    m.payload?.destino || m.payload?.telefone || m.payload?.email || m.consumer || '—';

  // A entidade Outbox não tem um campo "agregado" explícito — o worker só
  // grava tenant/event/payload/consumer. O identificador do agregado de
  // domínio (a mensagem, no único produtor hoje: whatsapp.send) vive dentro
  // do payload. Deriva-se por essa ordem de prioridade; na ausência de
  // qualquer id conhecido, mostra um traço em vez de um identificador
  // improvisado (o consumidor não é um id de agregado, então não entra
  // como fallback).
  const agregadoDe = (m: Outbox) =>
    m.payload?.mensagemId || m.payload?.campanhaId || m.payload?.solicitacaoId || '—';

  return (
    <div className="page" id="p-outbox">
      <div className="head">
        <div>
          <h1>Eventos &amp; Outbox</h1>
          <p>
            {/*
              proto:789 fecha com "falha ruidosa — nunca zero linhas em
              silêncio", afirmação sobre despacho com RLS que o worker não faz:
              ele lê a fila inteira e usa o `tenant_id` gravado em cada evento.
              Outbox transacional e SKIP LOCKED são literais e verdadeiros —
              `setLock('pessimistic_write').setOnLocked('skip_locked')` dentro
              da transação do lote, em `worker/outbox.worker.ts`.
            */}
            Integração entre módulos por eventos de domínio com outbox transacional: cada mensagem é
            gravada na mesma transação do fato que a originou. O despachante lê a fila com SKIP
            LOCKED, então mais de uma instância roda sem pegar o mesmo evento.
          </p>
        </div>
        <div className="act">
          <button
            className="btn"
            onClick={() => {
              if (
                window.confirm(
                  `Devolver ${comErro} evento(s) em falha para a fila?

O worker vai tentar enviar de novo. Eventos cancelados por opt-out ou teto mensal não são reprocessados.`,
                )
              ) {
                reprocessar.mutate(undefined);
              }
            }}
            disabled={comErro === 0 || reprocessar.isPending}
            title={
              comErro === 0
                ? 'Nenhum evento em falha'
                : 'Zera as tentativas e devolve para a fila'
            }
          >
            {reprocessar.isPending ? 'Reprocessando...' : 'Reprocessar falhas'}
          </button>
          <button className="btn" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* O recorte de 50 vale para os KPIs e para a tabela abaixo — é o
          mesmo teto que o backend aplica (`findOutbox`, take: 50). A tela
          de Auditoria já expõe esse aviso (AuditPage.tsx); o Outbox não
          pode ser menos transparente. */}
      <div className="note" style={{ marginBottom: 12, fontSize: 12 }}>
        Os indicadores e a lista abaixo cobrem os últimos 50 eventos do mandato, não o histórico completo.
      </div>

      {/* KPIs — mesmo conjunto de 4 do protótipo (proto:790) */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Pendentes</div>
          <div className="v">{contar('pendente')}</div>
          {/* O protótipo crava "lag médio 1,8 s" como número de demonstração;
              não temos essa métrica (latência de fila) medida no backend, e
              um número inventado seria pior que a ausência do dado. */}
          <div className="d">aguardando o worker</div>
        </div>
        <div className="card kpi">
          <div className="l">
            Despachados · 24h
            <Info titulo="Por que não há taxa de entrega nem latência">
              A fila registra o que a plataforma enfileirou. Taxa de entrega e latência dependem da
              integração com o WhatsApp Cloud API, que ainda não está conectada.
            </Info>
          </div>
          <div className="v" style={{ color: 'var(--good)' }}>
            {despachados24h}
          </div>
          <div className="d">idempotência garantida</div>
        </div>
        <div className="card kpi">
          <div className="l">Falhas com retry</div>
          <div className="v" style={{ color: falhasComRetry ? 'var(--warn)' : 'var(--ink)' }}>
            {falhasComRetry}
          </div>
          <div className="d">backoff exponencial</div>
        </div>
        <div className="card kpi">
          <div className="l">Sem contexto de tenant</div>
          {/* O protótipo mostra '0' como se fosse uma contagem monitorada.
              Aqui não há o que contar: tenant_id é NOT NULL na tabela
              (core.outbox) e toda consulta já filtra por tenant
              (findOutbox faz WHERE tenant_id = :tenantId) — um evento sem
              tenant não existe na origem, muito menos chega a esta tela.
              Um "0" nesse card seria um contador que nunca poderia dar
              outro valor: parece monitoramento, mas não é. Por isso o
              card afirma a garantia estrutural em vez de fingir medi-la. */}
          <div className="v" style={{ color: 'var(--good)', fontSize: 15, lineHeight: 1.3 }}>
            Impossível por construção
          </div>
          <div className="d">tenant_id é NOT NULL · consulta sempre filtra por tenant</div>
        </div>
      </div>

      {/* Filtros */}
      {status.length > 0 && (
        <div className="filters">
          <span
            className={`chip neutral chipbtn ${statusFiltro === '' ? 'on' : ''}`}
            onClick={() => setStatusFiltro('')}
          >
            Todos ({mensagens.length})
          </span>
          {status.map((s) => (
            <span
              key={s}
              className={`chip neutral chipbtn ${statusFiltro === s ? 'on' : ''}`}
              onClick={() => setStatusFiltro(s)}
            >
              {s} ({contar(s)})
            </span>
          ))}
        </div>
      )}

      {/* Tabela */}
      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th>Quando</th>
              <th>Evento</th>
              <th>Tenant</th>
              <th>Agregado</th>
              <th>Destino / consumidor</th>
              <th style={{ textAlign: 'center' }}>Tentativas</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((m) => (
              <tr key={m.id} className="row">
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                  {carimbo(m.created_at)}
                </td>
                <td>
                  <b className="mono" style={{ fontSize: 12 }}>
                    {m.event}
                  </b>
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {m.tenant_id || '—'}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {agregadoDe(m)}
                </td>
                <td style={{ fontSize: 12 }}>
                  {destinoDe(m)}
                  {m.last_error && (
                    <div style={{ color: 'var(--crit)', fontSize: 11 }}>{m.last_error}</div>
                  )}
                </td>
                <td className="num" style={{ textAlign: 'center' }}>
                  {m.retries ?? 0}
                </td>
                <td>
                  <span className={`chip ${CHIP[m.status] || 'neutral'}`}>{m.status}</span>
                </td>
              </tr>
            ))}

            {isLoading && (
              <tr>
                <td colSpan={7} className="empty">
                  Carregando a fila...
                </td>
              </tr>
            )}
            {!isLoading && lista.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  {mensagens.length === 0
                    ? 'Fila vazia. Eventos aparecem aqui quando campanhas e réguas geram disparos.'
                    : 'Nenhum evento com esse status.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
